import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import Alert from '@v2/shared/components/Alert';
import AlternateProductOffer from './AlternateProductOffer';
import Box from './Box';
import NextBillDate from './NextBillDate';
import PaymentMethod from './PaymentMethod';
import useAutoRenewalUrls from './urls';
import { AutoRenewalDetail, Props } from './types';

type DetailsProps = Pick<Props, 'autoRenewServiceName' | 'confirmUrlTemplate' | 'updatePaymentUrlTemplate'> & {
  autoRenewal: AutoRenewalDetail;
  /** Opens the disable flow. Owned by the page — see ManageAutoRenewal. */
  onDisable: () => void;
  /** Opens the delay flow, on the statuses that offer it. */
  onDelay: () => void;
};

/**
 * What is going on with this subscription, and therefore what there is to do about it.
 *
 * One switch on the status, and a second on `type` for the one status that has more than
 * one story behind it: a subscription needs attention because the payment failed, because
 * the title has gone and there is something to move to, or because it has gone and the
 * only thing left is a higher price.
 *
 * The two flows are opened from here but do not live here. That matters: disabling turns
 * the subscription `canceled`, which lands on the branch below that draws no controls at
 * all — so a modal mounted under this switch would unmount itself the moment it
 * succeeded, and the customer would never see the confirmation. The page owns them.
 *
 * Two statuses draw nothing here, both as at the source. `canceled` and
 * `no-longer-available` get their sentence and no controls, which is right — there is
 * nothing left to change. `charged` draws nothing at all, not even a sentence, which
 * reads as an oversight rather than a decision: the list calls a just-charged
 * subscription Active, and this page then offers its owner no way to disable it. Ported
 * as found rather than quietly fixed — it is a product question, not a porting one.
 */
export default function Details({ autoRenewal, ...props }: DetailsProps) {
  const { confirmUrl } = useAutoRenewalUrls();
  const { autoRenewServiceName, confirmUrlTemplate } = props;
  const upcoming = autoRenewal.upcoming_transaction;

  switch (autoRenewal.status) {
    case 'active':
      return (
        <>
          <Alert color="success" className="mb-7">
            <div className="font-semibold mb-1">Active</div>
            Your subscription is active and will renew on the date specified below.
          </Alert>
          <NextBillDate autoRenewal={autoRenewal} {...props} />
          <PaymentMethod autoRenewal={autoRenewal} {...props} />
        </>
      );

    case 'canceled':
      return (
        <Alert color="danger" className="mb-7">
          <div className="font-semibold mb-1">Canceled</div>
          Your {autoRenewServiceName} subscription is canceled and you will no longer be charged.
        </Alert>
      );

    case 'no-longer-available':
      return (
        <Alert color="danger" className="mb-7">
          <div className="font-semibold mb-1">No Longer Available</div>
          Unfortunately, this magazine is no longer available on our website. You will no longer be charged.
        </Alert>
      );

    case 'needs-attention':
      switch (autoRenewal.type) {
        case 'available-alt-prod':
          return (
            <div className="mb-7">
              <Alert color="warning" className="mb-3">
                <div className="font-semibold mb-1">No Longer Available</div>
                Unfortunately, {autoRenewal.product.name} is no longer available.
              </Alert>
              <NextBillDate autoRenewal={autoRenewal} {...props} />
              <AlternateProductOffer autoRenewal={autoRenewal} confirmUrlTemplate={confirmUrlTemplate} />
            </div>
          );

        case 'available-high-price':
          return (
            <>
              <Alert color="warning" className="mb-3">
                <div className="font-semibold mb-1">Available At Higher Price</div>
                Unfortunately we were unable to secure your {autoRenewServiceName} price, and your magazine
                subscription will not be automatically renewed.
              </Alert>
              <NextBillDate autoRenewal={autoRenewal} {...props} />
              <Box>
                <h3 className="font-semibold mt-0">Don&apos;t miss a single issue</h3>
                <div className="font-medium text-slate-700 text-sm mb-3">
                  Renew {autoRenewal.product.name} today at our current rate, and we&apos;ll ensure you don&apos;t
                  miss any issues.
                </div>
                {upcoming && (
                  <a
                    href={confirmUrl(confirmUrlTemplate, upcoming)}
                    className="font-semibold text-sm text-sky-600 hover:text-sky-700 active:text-sky-800 hover:underline"
                  >
                    Renew Now
                    <FontAwesomeIcon icon={faAngleRight} className="ml-1" />
                  </a>
                )}
              </Box>
            </>
          );

        case 'available':
          return (
            <>
              <Alert color="warning" className="mb-7">
                <div className="font-semibold mb-1">Action Required</div>
                There&apos;s an issue with your payment method. Update your payment method to ensure automatic
                renewal.
              </Alert>
              <NextBillDate autoRenewal={autoRenewal} {...props} />
              <PaymentMethod autoRenewal={autoRenewal} hasIssueWithPayment {...props} />
            </>
          );

        default:
          return null;
      }

    default:
      return null;
  }
}
